/**
 * Second Opinion — the observer's form (X-OBSERVER-001).
 *
 * Public route, reached only through a secret one-time link. The observer has
 * no account and sees nothing about the learner beyond their first name.
 *
 * Design constraints that are not negotiable here:
 *   - Not one question asks for a rating, a score, or a judgement of
 *     character. Every prompt asks what they have *watched happen*.
 *   - The consent promise is on screen before they type a word, not buried at
 *     the end: the young person reads everything and decides what counts.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2, Send, ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  OBSERVER_MAX_CAUSES, OBSERVER_MAX_PASSIONS, OBSERVER_PROMPTS,
  observerCauseCatalogue, observerPassionCatalogue, relationshipNoun,
  type ObserverPrompt,
} from "@/features/assessment-v2/observer";

/** Non-2xx replies surface as FunctionsHttpError with the body unparsed, so
 *  the reason code lives in `context` — without this every expired link would
 *  read as "doesn't work", which sends people looking for a typo. */
async function errorCode(err: unknown, data: unknown): Promise<string | undefined> {
  const fromData = (data as { error?: string } | null)?.error;
  if (fromData) return fromData;
  const ctx = (err as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.json === "function") {
    try { return ((await ctx.json()) as { error?: string })?.error; } catch { /* body not json */ }
  }
  return undefined;
}

type Phase = "loading" | "invalid" | "expired" | "used" | "form" | "sent";

interface LoadedInvite {
  learner_first_name: string;
  relationship: string;
  observer_name: string | null;
}

function PickChip({
  label, hint, active, disabled, onToggle,
}: { label: string; hint?: string; active: boolean; disabled: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled && !active}
      aria-pressed={active}
      className={[
        "min-h-[48px] w-full rounded-2xl border px-4 py-3 text-left transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        active
          ? "border-primary bg-primary/15 text-foreground"
          : "border-border bg-card text-foreground hover:border-primary/50",
        disabled && !active ? "cursor-not-allowed opacity-40" : "",
      ].join(" ")}
    >
      <span className="flex items-start gap-2">
        <span
          aria-hidden
          className={[
            "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border",
            active ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
          ].join(" ")}
        >
          {active ? <Check className="h-3.5 w-3.5" /> : null}
        </span>
        <span>
          <span className="block text-sm font-medium leading-snug">{label}</span>
          {hint ? <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span> : null}
        </span>
      </span>
    </button>
  );
}

export default function ObserverInput() {
  const { token = "" } = useParams();
  const [phase, setPhase] = useState<Phase>("loading");
  const [invite, setInvite] = useState<LoadedInvite | null>(null);
  const [observerName, setObserverName] = useState("");
  const [notes, setNotes] = useState<Partial<Record<ObserverPrompt["id"], string>>>({});
  const [passionIds, setPassionIds] = useState<string[]>([]);
  const [causeIds, setCauseIds] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: fnErr } = await supabase.functions.invoke("observer-submit", {
        body: { token, action: "load" },
      });
      if (cancelled) return;
      const payload = data as (LoadedInvite & { error?: string }) | null;
      if (fnErr || payload?.error) {
        const code = await errorCode(fnErr, data);
        if (cancelled) return;
        setPhase(code === "link_expired" ? "expired" : code === "already_submitted" ? "used" : "invalid");
        return;
      }
      setInvite(payload as LoadedInvite);
      setObserverName(payload?.observer_name ?? "");
      setPhase("form");
    })();
    return () => { cancelled = true; };
  }, [token]);

  const causes = useMemo(() => observerCauseCatalogue(), []);
  const passions = useMemo(() => {
    const all = observerPassionCatalogue().filter((p) => !causes.some((c) => c.id === p.id));
    const q = query.trim().toLowerCase();
    if (!q) return all.slice(0, 24);
    return all.filter((p) =>
      p.label.toLowerCase().includes(q) || p.category.toLowerCase().includes(q),
    ).slice(0, 40);
  }, [query, causes]);

  const who = invite?.learner_first_name ?? "them";
  const anythingGiven =
    passionIds.length > 0 || causeIds.length > 0 ||
    Object.values(notes).some((v) => (v ?? "").trim().length > 0);

  function toggle(list: string[], setList: (v: string[]) => void, id: string, max: number) {
    setList(list.includes(id) ? list.filter((x) => x !== id)
      : list.length >= max ? list : [...list, id]);
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    const { data, error: fnErr } = await supabase.functions.invoke("observer-submit", {
      body: {
        token,
        action: "submit",
        observer_name: observerName.trim() || undefined,
        passion_ids: passionIds,
        cause_ids: causeIds,
        notes,
      },
    });
    setSubmitting(false);
    const payload = data as { ok?: boolean; error?: string } | null;
    if (fnErr || payload?.error) {
      const code = await errorCode(fnErr, data);
      setError(
        code === "already_submitted"
          ? "This has already been sent — thank you."
          : "That didn't send. Check your connection and try again.",
      );
      return;
    }
    setPhase("sent");
  }

  if (phase === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background p-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </main>
    );
  }

  if (phase !== "form" && phase !== "sent") {
    const copy = {
      invalid: { h: "This link doesn't work", p: "It may have been mistyped. Ask for a fresh one." },
      expired: { h: "This link has expired", p: "Links last 30 days. Ask them to send a new one — it takes a second." },
      used: { h: "Already sent — thank you", p: "Your answers are with them. They'll decide what becomes part of their results." },
    }[phase];
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-3 p-6">
        <h1 className="text-2xl font-semibold">{copy.h}</h1>
        <p className="text-muted-foreground">{copy.p}</p>
      </main>
    );
  }

  if (phase === "sent") {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 p-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15">
          <Check className="h-7 w-7 text-primary" aria-hidden />
        </div>
        <h1 className="text-2xl font-semibold">Thank you — that's with {who}</h1>
        <p className="text-muted-foreground">
          They'll read every word and choose what becomes part of their results.
          What you've noticed can open up work they'd never have thought to look at.
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-5 pb-32 pt-10">
      <header className="mb-8">
        <p className="text-sm font-medium text-primary">WeKIT Career Clarity 360</p>
        <h1 className="mt-2 text-3xl font-semibold leading-tight">
          What have you noticed about {who}?
        </h1>
        <p className="mt-3 text-muted-foreground">
          {who} is working out what kind of work would suit them, and asked for
          your view {relationshipNoun(invite?.relationship) === "your parent" ? "as their parent" : "as someone who knows them"}.
          About five minutes. Nothing to rate, no right answers — just what
          you've actually seen.
        </p>
        <div className="mt-5 flex gap-3 rounded-2xl border border-border bg-card p-4">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">{who} stays in charge.</strong>{" "}
            They read everything you send and tick line by line what counts.
            Anything they don't recognise, they set aside — and you won't see
            their answers or results unless they share them.
          </p>
        </div>
      </header>

      <section className="mb-10 space-y-2">
        <Label htmlFor="observer-name">Your name</Label>
        <Input
          id="observer-name"
          value={observerName}
          onChange={(e) => setObserverName(e.target.value.slice(0, 80))}
          placeholder="So they know who this is from"
          className="min-h-[48px]"
        />
      </section>

      <section className="mb-10 space-y-6" aria-labelledby="prompts-heading">
        <h2 id="prompts-heading" className="text-xl font-semibold">In your words</h2>
        {OBSERVER_PROMPTS.map((p) => (
          <div key={p.id} className="space-y-2">
            <Label htmlFor={`prompt-${p.id}`} className="text-base">{p.question}</Label>
            <p className="text-sm text-muted-foreground">{p.help}</p>
            <Textarea
              id={`prompt-${p.id}`}
              value={notes[p.id] ?? ""}
              maxLength={p.maxLength}
              rows={3}
              onChange={(e) => setNotes((n) => ({ ...n, [p.id]: e.target.value }))}
              className="rounded-2xl"
            />
          </div>
        ))}
      </section>

      <section className="mb-10" aria-labelledby="causes-heading">
        <h2 id="causes-heading" className="text-xl font-semibold">
          What do they care about in the world?
        </h2>
        <p className="mb-4 mt-1 text-sm text-muted-foreground">
          Pick up to {OBSERVER_MAX_CAUSES}. {causeIds.length}/{OBSERVER_MAX_CAUSES} chosen.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {causes.map((c) => (
            <PickChip
              key={c.id}
              label={c.label}
              hint={c.description}
              active={causeIds.includes(c.id)}
              disabled={causeIds.length >= OBSERVER_MAX_CAUSES}
              onToggle={() => toggle(causeIds, setCauseIds, c.id, OBSERVER_MAX_CAUSES)}
            />
          ))}
        </div>
      </section>

      <section className="mb-10" aria-labelledby="passions-heading">
        <h2 id="passions-heading" className="text-xl font-semibold">
          What do you see them choose to do?
        </h2>
        <p className="mb-3 mt-1 text-sm text-muted-foreground">
          Pick up to {OBSERVER_MAX_PASSIONS} — only things you've actually watched
          them do. {passionIds.length}/{OBSERVER_MAX_PASSIONS} chosen.
        </p>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search — drawing, coding, animals, football…"
          aria-label="Search activities"
          className="mb-3 min-h-[48px]"
        />
        <div className="grid gap-2 sm:grid-cols-2">
          {passions.map((p) => (
            <PickChip
              key={p.id}
              label={p.label}
              hint={p.category}
              active={passionIds.includes(p.id)}
              disabled={passionIds.length >= OBSERVER_MAX_PASSIONS}
              onToggle={() => toggle(passionIds, setPassionIds, p.id, OBSERVER_MAX_PASSIONS)}
            />
          ))}
          {passions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing matches that word — try another.</p>
          ) : null}
        </div>
      </section>

      {error ? (
        <p role="alert" className="mb-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm">
          {error}
        </p>
      ) : null}

      <div className="fixed inset-x-0 bottom-0 border-t border-border bg-background/95 p-4 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <p className="flex-1 text-xs text-muted-foreground">
            {anythingGiven ? "Ready to send." : "Answer at least one thing to send."}
          </p>
          <Button
            onClick={submit}
            disabled={!anythingGiven || submitting}
            className="min-h-[48px] rounded-2xl px-6"
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send to {who}
          </Button>
        </div>
      </div>
    </main>
  );
}
